import React, { useId, useState } from "react"
import { Input } from "antd"

export interface DraftInputProps {
  /** The stored text. */
  value: string
  /** Writes the text; returns the sentence to show when it is refused, or null once written. */
  onCommit: (text: string) => string | null
  "aria-label": string
  placeholder?: string
  className?: string
}

/**
 * A text field that writes when it is left or on Enter, not on each
 * keystroke, so a rule that tidies or refuses the text never fights the
 * typing. A refused text stays in the field, with the reason under it, and
 * the stored text stays as it was.
 */
const DraftInput: React.FC<DraftInputProps> = ({
  value,
  onCommit,
  placeholder,
  className,
  "aria-label": label,
}) => {
  // null while the field shows the stored text.
  const [draft, setDraft] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const errorId = useId()

  const commit = (): void => {
    if (draft === null) return
    const refusal = onCommit(draft)
    setError(refusal)
    if (!refusal) setDraft(null)
  }

  return (
    <div className={className ? `ts-draft ${className}` : "ts-draft"}>
      <Input
        size="small"
        aria-label={label}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        placeholder={placeholder}
        value={draft ?? value}
        status={error ? "error" : undefined}
        onChange={(e) => {
          setDraft(e.target.value)
          setError(null)
        }}
        onBlur={commit}
        onPressEnter={commit}
      />
      {error && (
        <p id={errorId} className="ts-draft__error" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

export default DraftInput
